/**
 * AK-389 — витрина осмотров поля на вебе. С 16.08.2026 (founder «делаем а
 * сначала») веб умеет и создавать осмотр (см. `InspectionCreateSheet.tsx`),
 * не только просматривать — но осмотр остаётся append-only (ADR-001):
 * редактирования/удаления после создания нет ни здесь, ни на мобилке.
 *
 * Pure-хелперы витрины: сводка находок, чип источника, лейблы фенофазы/степени,
 * проекция контура поля + GPS-точки в SVG-мини-карту. Держим их отдельно от
 * компонентов, чтобы юнит-тестить без монтирования DOM.
 *
 * Модель данных зеркалит backend `InspectionOut` (schemas/inspections.py):
 * структура осмотра лежит внутри `ai_extracted` JSONB (ADR-042), дискриминатор
 * источника — `ai_extracted.source_kind` (mobile_manual / voice_scout /
 * web_manual).
 */

export type ProblemType = 'pest' | 'disease' | 'weed';
export type Severity = 'low' | 'medium' | 'high';

export interface InspectionProblem {
  type: ProblemType | string;
  name: string;
  severity?: Severity | string | null;
}

export interface InspectionAiExtracted {
  source_kind?: string | null;
  phase?: string | null;
  no_problems?: boolean;
  problems?: InspectionProblem[];
  recommendations?: string[];
  note?: string | null;
}

export interface InspectionPhoto {
  url?: string;
  lat?: number | null;
  lng?: number | null;
  [k: string]: unknown;
}

export interface InspectionLocation {
  lat: number;
  lng: number;
}

export interface Inspection {
  id: string;
  field_id?: string | null;
  inspected_at: string;
  inspector_user_id?: string | null;
  inspector_name?: string | null;
  raw_transcript?: string | null;
  ai_extracted?: InspectionAiExtracted | null;
  photos?: InspectionPhoto[] | null;
  location?: InspectionLocation | null;
  created_at: string;
  updated_at: string;
}

// ─── Мелкие лейблы ──────────────────────────────────────────────────────

const SEVERITY_FULL: Record<Severity, string> = {
  low: 'слабая',
  medium: 'средняя',
  high: 'сильная',
};

const SEVERITY_SHORT: Record<Severity, string> = {
  low: 'сл.',
  medium: 'ср.',
  high: 'сильн.',
};

const PROBLEM_ICON: Record<ProblemType, string> = {
  pest: '🐛',
  disease: '🦠',
  weed: '🌿',
};

export function severityFull(sev?: string | null): string | null {
  if (!sev) return null;
  return SEVERITY_FULL[sev as Severity] ?? sev;
}

export function severityShort(sev?: string | null): string | null {
  if (!sev) return null;
  return SEVERITY_SHORT[sev as Severity] ?? sev;
}

export function problemIcon(type: string): string {
  return PROBLEM_ICON[type as ProblemType] ?? '•';
}

// ─── Создание осмотра (форма) ──────────────────────────────────────────

export const PROBLEM_TYPE_LABEL: Record<ProblemType, string> = {
  pest: 'Вредитель',
  disease: 'Болезнь',
  weed: 'Сорняк',
};

export const SEVERITY_OPTIONS: { value: Severity; label: string }[] = [
  { value: 'low', label: 'слабая' },
  { value: 'medium', label: 'средняя' },
  { value: 'high', label: 'сильная' },
];

/**
 * Минимум для сохранения осмотра (мобильный FR-4.6, зеркалим 1:1 —
 * `InspectionDraft.isValid()` в shared/domain/model/Inspection.kt):
 * хотя бы одна находка ИЛИ непустая заметка.
 */
export function isDraftValid(problems: InspectionProblem[], note: string): boolean {
  return problems.length > 0 || note.trim().length > 0;
}

export function inspectorLabel(insp: Inspection): string {
  return insp.inspector_name?.trim() || 'Автор неизвестен';
}

export function phaseLabel(insp: Inspection): string | null {
  const p = insp.ai_extracted?.phase;
  return p && p.trim() ? p.trim() : null;
}

export function photoCount(insp: Inspection): number {
  return insp.photos?.length ?? 0;
}

// ─── Источник (source_kind) → чип ──────────────────────────────────────

export type ChipVariant = 'primary' | 'accent-brand' | 'secondary' | 'outline';

export interface SourceChip {
  label: string;
  variant: ChipVariant;
}

/**
 * Чип источника осмотра. Известные источники получают иконку + фирменный цвет,
 * неизвестный/отсутствующий — нейтральный чип (задача AK-389).
 *
 * 'web_manual' (founder 16.08.2026) — осмотр, записанный на вебе (та же
 * форма/семантика, что мобильная). Отдельный вариант чипа `outline`, чтобы не
 * путать с «неизвестный источник» (`secondary`).
 */
export function sourceChip(insp: Inspection): SourceChip {
  switch (insp.ai_extracted?.source_kind) {
    case 'mobile_manual':
      return { label: '📱 мобильный', variant: 'accent-brand' };
    case 'voice_scout':
      return { label: '🎙 голосовой', variant: 'primary' };
    case 'web_manual':
      return { label: '💻 веб', variant: 'outline' };
    default:
      return { label: 'осмотр', variant: 'secondary' };
  }
}

// ─── Сводка находок ────────────────────────────────────────────────────

export type FindingsTone = 'ok' | 'problem' | 'none';

export interface FindingsSummary {
  tone: FindingsTone;
  /** Текст для строки списка. Пустой при tone==='none'. */
  text: string;
}

function problemsOf(insp: Inspection): InspectionProblem[] {
  return (insp.ai_extracted?.problems ?? []).filter((p) => p && p.name);
}

/**
 * Однострочная сводка находок для строки списка.
 *   - есть находки → "клоп вредная черепашка (ср.) · овсюг (сильн.)" (problem)
 *   - явно «проблем нет» ИЛИ находок нет при no_problems=true → "проблем нет" (ok)
 *   - вообще нет данных о находках → пусто (none) — строка их не показывает
 */
export function findingsSummary(insp: Inspection): FindingsSummary {
  const problems = problemsOf(insp);
  if (problems.length) {
    const text = problems
      .map((p) => {
        const sev = severityShort(p.severity);
        return sev ? `${p.name} (${sev})` : p.name;
      })
      .join(' · ');
    return { tone: 'problem', text };
  }
  if (insp.ai_extracted?.no_problems) {
    return { tone: 'ok', text: 'проблем нет' };
  }
  return { tone: 'none', text: '' };
}

export function hasFindingsBlock(insp: Inspection): boolean {
  return problemsOf(insp).length > 0 || insp.ai_extracted?.no_problems === true;
}

export function structuredProblems(insp: Inspection): InspectionProblem[] {
  return problemsOf(insp);
}

export function recommendationsOf(insp: Inspection): string[] {
  return (insp.ai_extracted?.recommendations ?? []).filter((r) => r && r.trim());
}

export function noteOf(insp: Inspection): string | null {
  const n = insp.ai_extracted?.note;
  return n && n.trim() ? n.trim() : null;
}

export function photosWithUrl(insp: Inspection): InspectionPhoto[] {
  return (insp.photos ?? []).filter((p) => typeof p.url === 'string' && p.url);
}

// ─── Дата / время ──────────────────────────────────────────────────────

function toDate(iso?: string | null): Date | null {
  if (!iso) return null;
  const d = new Date(iso);
  return isNaN(d.getTime()) ? null : d;
}

/** "07.07.2026" — локальная дата осмотра. */
export function formatInspectionDate(iso?: string | null): string {
  const d = toDate(iso);
  if (!d) return '';
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  return `${dd}.${mm}.${d.getFullYear()}`;
}

/** "07.07.2026 14:25" — дата + локальное время для заголовка деталки. */
export function formatInspectionDateTime(iso?: string | null): string {
  const d = toDate(iso);
  if (!d) return '';
  const hh = String(d.getHours()).padStart(2, '0');
  const mi = String(d.getMinutes()).padStart(2, '0');
  return `${formatInspectionDate(iso)} ${hh}:${mi}`;
}

// ─── Проекция контура + точки в SVG (мини-карта) ────────────────────────

export interface XY {
  x: number;
  y: number;
}

export interface GeoBounds {
  minLon: number;
  maxLon: number;
  minLat: number;
  maxLat: number;
}

type Bounds = GeoBounds;

/**
 * AK-459: географическое соотношение сторон bbox (ширина/высота НА ЗЕМЛЕ).
 *
 * На широте φ градус долготы короче градуса широты в cos(φ) раз (≈0.68 на
 * наших 46–48°), поэтому «ширина/высота» bbox в градусах ≠ пропорции поля.
 * aspect = (Δlon · cos(φ_center)) / Δlat. Вырожденный bbox (Δlat=0 или
 * Δlon=0) → 1 (fail-safe: рисуем как раньше, по центру).
 */
export function geoAspectRatio(b: GeoBounds): number {
  const spanLon = b.maxLon - b.minLon;
  const spanLat = b.maxLat - b.minLat;
  if (spanLon <= 0 || spanLat <= 0) return 1;
  const midLatRad = (((b.minLat + b.maxLat) / 2) * Math.PI) / 180;
  // cos → 0 у полюсов; клампим, чтобы не выродить аспект в 0 на битых данных.
  const cos = Math.max(Math.cos(midLatRad), 1e-6);
  return (spanLon * cos) / spanLat;
}

/**
 * Разворачивает GeoJSON Polygon / MultiPolygon в набор внешних колец
 * ([lon, lat][]). Иные типы / битые данные → пустой массив (мини-карта скроется
 * либо покажет только точку).
 */
export function geojsonToRings(
  geojson?: { type?: string; coordinates?: unknown } | null,
): number[][][] {
  if (!geojson || !geojson.coordinates) return [];
  const rings: number[][][] = [];
  const pushPolygon = (poly: unknown) => {
    if (!Array.isArray(poly)) return;
    for (const ring of poly) {
      if (!Array.isArray(ring)) continue;
      const pts = ring.filter(
        (c): c is number[] =>
          Array.isArray(c) &&
          c.length >= 2 &&
          typeof c[0] === 'number' &&
          typeof c[1] === 'number',
      );
      if (pts.length >= 3) rings.push(pts);
    }
  };
  if (geojson.type === 'Polygon') {
    pushPolygon(geojson.coordinates);
  } else if (geojson.type === 'MultiPolygon') {
    for (const poly of geojson.coordinates as unknown[]) pushPolygon(poly);
  }
  return rings;
}

function boundsOf(rings: number[][][], point?: InspectionLocation | null): Bounds | null {
  let minLon = Infinity;
  let maxLon = -Infinity;
  let minLat = Infinity;
  let maxLat = -Infinity;
  const acc = (lon: number, lat: number) => {
    if (lon < minLon) minLon = lon;
    if (lon > maxLon) maxLon = lon;
    if (lat < minLat) minLat = lat;
    if (lat > maxLat) maxLat = lat;
  };
  for (const ring of rings) for (const [lon, lat] of ring) acc(lon, lat);
  if (point) acc(point.lng, point.lat);
  if (!isFinite(minLon) || !isFinite(minLat)) return null;
  return { minLon, maxLon, minLat, maxLat };
}

export interface MiniMapProjection {
  size: number;
  ringsXY: XY[][];
  pointXY: XY | null;
}

/**
 * Проецирует кольца контура и (опц.) GPS-точку в квадратный viewBox size×size
 * с отступом pad. Долгота → x, широта → y (инвертирована). Вырожденный bbox
 * (одна точка / нулевая ширина) центрируется. Возвращает null, если рисовать
 * нечего (ни контура, ни точки).
 *
 * AK-459: с географической аспект-коррекцией. Раньше lon и lat растягивались
 * в inner×inner НЕЗАВИСИМО (stretch в квадрат) — поле плющилось: на широте
 * 46–48° градус долготы ≈ 0.68 градуса широты. Теперь оба span приводятся к
 * «земным» единицам (Δlon·cos(φ_center) vs Δlat), берётся единый масштаб
 * (contain) и рисунок центрируется letterbox'ом внутри квадрата — пропорции
 * совпадают с реальной формой поля.
 */
export function projectMiniMap(
  rings: number[][][],
  point?: InspectionLocation | null,
  size = 100,
  pad = 6,
): MiniMapProjection | null {
  const b = boundsOf(rings, point);
  if (!b) return null;
  const spanLon = b.maxLon - b.minLon;
  const spanLat = b.maxLat - b.minLat;
  const inner = size - 2 * pad;
  // «Земные» размеры bbox (в градусах широты): по x долгота сжата на cos(φ).
  const midLatRad = (((b.minLat + b.maxLat) / 2) * Math.PI) / 180;
  const cos = Math.max(Math.cos(midLatRad), 1e-6);
  const spanX = spanLon * cos;
  const spanY = spanLat;
  const maxSpan = Math.max(spanX, spanY);
  // Единый масштаб по обеим осям (contain) + центрирование (letterbox).
  const scale = maxSpan > 0 ? inner / maxSpan : 0;
  const offX = pad + (inner - spanX * scale) / 2;
  const offY = pad + (inner - spanY * scale) / 2;
  const project = (lon: number, lat: number): XY => {
    if (scale === 0) return { x: size / 2, y: size / 2 };
    const x = offX + (lon - b.minLon) * cos * scale;
    const y = offY + (b.maxLat - lat) * scale;
    return { x, y };
  };
  const ringsXY = rings.map((ring) => ring.map(([lon, lat]) => project(lon, lat)));
  const pointXY = point ? project(point.lng, point.lat) : null;
  if (!ringsXY.length && !pointXY) return null;
  return { size, ringsXY, pointXY };
}

export function ringToPath(ring: XY[]): string {
  if (!ring.length) return '';
  return (
    ring.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x.toFixed(2)} ${p.y.toFixed(2)}`).join(' ') +
    ' Z'
  );
}
